import { Tooltip } from "@mui/material";
import DownloadIcon from "@redotech/redo-icons/download-02_filled.svg";
import AttachmentIcon from "@redotech/redo-icons/file-type-attachment_solid.svg";
import AudioSvg from "@redotech/redo-icons/file-type-audio_solid.svg";
import DocumentIcon from "@redotech/redo-icons/file-type-document_solid.svg";
import FolderIcon from "@redotech/redo-icons/file-type-folder_solid.svg";
import PDFIcon from "@redotech/redo-icons/file-type-pdf_solid.svg";
import VideoSvg from "@redotech/redo-icons/file-type-video-02_solid.svg";
import { ReactElement, memo, useState } from "react";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import * as attachmentThumbnailCss from "./attachment-thumbnail.module.css";
import { ImageLightbox } from "./image-lightbox";
import { Lightbox } from "./lightbox";

interface FileThumbnailProps {
  url: string;
  description?: string;
  mimeType: string | undefined;
  icon?: ReactElement;
  typeName: string;
  showTooltip?: boolean;
  restrictHeight?: boolean;
  restrictWidth?: boolean;
  /** "sm" renders a more compact box (smaller icon, padding, and text). */
  size?: "sm" | "md";
}

const fileInfo = new Map<
  RegExp,
  {
    Element: (props: FileThumbnailProps) => JSX.Element;
    icon: ReactElement;
    typeName: string;
  }
>([
  [
    /audio.*/,
    {
      Element: (props) => <AudioVideoThumbnail {...props} typeName="Audio" />,
      icon: <AudioSvg />,
      typeName: "Audio",
    },
  ],
  [
    /video.*/,
    {
      Element: (props) => <AudioVideoThumbnail {...props} typeName="Video" />,
      icon: <VideoSvg />,
      typeName: "Video",
    },
  ],
  [
    /image.*/,
    {
      Element: (props) => <ImageThumbnail {...props} />,
      icon: <AttachmentIcon />, // Unused, only included to conform to interface
      typeName: "Image",
    },
  ],
  [
    /text\/plain/,
    {
      Element: (props) => <FileThumbnail {...props} />,
      icon: <DocumentIcon />,
      typeName: "Text",
    },
  ],
  [
    /application\/pdf/,
    {
      Element: (props) => <FileThumbnail {...props} />,
      icon: <PDFIcon />,
      typeName: "PDF",
    },
  ],
  [
    /application\/zip/,
    {
      Element: (props) => <FileThumbnail {...props} />,
      icon: <FolderIcon />,
      typeName: "ZIP archive",
    },
  ],
  [
    /.*/,
    {
      Element: (props) => <FileThumbnail {...props} />,
      icon: <AttachmentIcon />,
      typeName: "File",
    },
  ],
]);
function getFileInfo(mimeType: string | undefined) {
  mimeType = mimeType || "";
  for (const [pattern, info] of fileInfo) {
    if (pattern.test(mimeType)) {
      return info;
    }
  }
  // Should never reach this line since the last regex matches all strings
  throw new Error("No file info found");
}

export const AttachmentThumbnail = memo(function AttachmentThumbnail(
  props: {
    url: string;
    mimeType: string | undefined;
  } & Partial<FileThumbnailProps>,
) {
  const { Element, icon, typeName } = getFileInfo(props.mimeType);
  return <Element icon={icon} typeName={typeName} {...props} />;
});

const ImageThumbnail = memo(function ImageThumbnail({
  url,
  description,
  mimeType,
  typeName,
  size,
}: FileThumbnailProps) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  return (
    <FileThumbnailContents
      description={description}
      icon={
        <>
          <img
            alt={description}
            className={attachmentThumbnailCss.image}
            onClick={() => setIsLightboxOpen(true)}
            src={url}
          />
          <ImageLightbox
            imageSrc={url}
            onClose={() => setIsLightboxOpen(false)}
            open={isLightboxOpen}
          />
        </>
      }
      mimeType={mimeType}
      onClick={() => {
        setIsLightboxOpen(true);
      }}
      size={size}
      typeName={typeName}
      url={url}
    />
  );
});

const AudioVideoThumbnail = memo(function AudioVideoThumbnail({
  url,
  description,
  mimeType,
  typeName,
  icon,
  size,
}: Omit<FileThumbnailProps, "typeName"> & { typeName: "Audio" | "Video" }) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  return (
    <>
      <FileThumbnailContents
        description={description}
        icon={icon}
        mimeType={mimeType}
        onClick={() => setIsLightboxOpen(true)}
        size={size}
        typeName={typeName}
        url={url}
      />
      <Lightbox onClose={() => setIsLightboxOpen(false)} open={isLightboxOpen}>
        {typeName === "Audio" ? (
          <audio controls>
            <source src={url} type={mimeType} />
            Your browser does not support the audio element.
          </audio>
        ) : (
          <video
            controls
            style={{
              maxWidth: window.innerWidth * 0.75,
              maxHeight: window.innerHeight * 0.75,
            }}
          >
            <source src={url} type={mimeType} />
            Your browser does not support the video element.
          </video>
        )}
      </Lightbox>
    </>
  );
});

const FileThumbnail = memo(function FileThumbnail({
  url,
  description,
  mimeType,
  icon,
  typeName,
  restrictHeight = true,
  restrictWidth,
  size,
}: FileThumbnailProps) {
  return (
    <a download={description} href={url}>
      <FileThumbnailContents
        description={description}
        icon={icon}
        mimeType={mimeType}
        restrictHeight={restrictHeight}
        restrictWidth={restrictWidth}
        size={size}
        typeName={typeName}
        url={url}
      />
    </a>
  );
});

const FileThumbnailContents = memo(function FileThumbnailContents({
  url,
  description,
  mimeType,
  icon,
  typeName,
  onClick,
  showTooltip,
  restrictHeight,
  restrictWidth,
  size = "md",
}: FileThumbnailProps & { onClick?: () => void }) {
  mimeType = mimeType || "";
  const isSmall = size === "sm";
  const handleDownload = (url: string) => {
    const link = document.createElement("a");
    link.download = description || url;
    link.href = url;
    link.target = "_blank";
    document.body.append(link);
    link.click();
    link.remove();
  };
  const containerClass = restrictHeight
    ? isSmall
      ? attachmentThumbnailCss.fileContainerFixedHeightSm
      : attachmentThumbnailCss.fileContainerFixedHeight
    : isSmall
      ? attachmentThumbnailCss.fileContainerSm
      : attachmentThumbnailCss.fileContainer;
  return (
    <div className={containerClass} onClick={onClick}>
      <div
        className={
          isSmall
            ? attachmentThumbnailCss.fileTypeSm
            : attachmentThumbnailCss.fileType
        }
      >
        {icon}
      </div>
      <div
        className={
          restrictWidth
            ? attachmentThumbnailCss.fileInfoWidthRestricted
            : attachmentThumbnailCss.fileInfo
        }
      >
        {description &&
          (showTooltip === false ? (
            <Tooltip title={description}>
              <p className={attachmentThumbnailCss.overflowEllipsis}>
                {description}
              </p>
            </Tooltip>
          ) : (
            <p className={attachmentThumbnailCss.overflowEllipsis}>
              {description}
            </p>
          ))}

        <p className={attachmentThumbnailCss.fileTypeName}>
          {typeName || mimeType}
        </p>
      </div>
      <div className={attachmentThumbnailCss.downloadButton}>
        <RedoButton
          hierarchy="tertiary"
          IconLeading={DownloadIcon}
          onClick={(e) => {
            e.stopPropagation();
            handleDownload(url);
          }}
          size={isSmall ? "xs" : "md"}
          theme="normal"
        />
      </div>
    </div>
  );
});
